import React, { Fragment, useEffect, useState, useRef } from 'react'
import Dropzone from 'react-dropzone'
import { useAuthentication } from 'hooks/useAuthentication'

// third party components
import { Dialog, Transition } from '@headlessui/react'

// next components
import { useViewport } from 'utils'

// custom components
import Input from 'components/Input'
import Textarea from 'components/Input/Textarea'
import GradientButton from 'components/Button/GradientButton'
import Alert from 'components/Alert'

// images and icons
import CloseIcon from 'assets/icons/close-white-black-15x15.svg?component'
import EditorGreenIcon from 'assets/icons/editor-white.svg?component'
import TrashBin from 'assets/icons/trash-bin-18x18.svg?component'

// styles
import styles from './index.module.scss'

// apis
import merchAPI from 'apis/merch'
import uploadFileToS3 from 'apis/aws'
import LoadingButton from 'components/Button/LoadingButton'

const EditMerchModal = props => {
  const { open, close, onSave } = props
  const viewport = useViewport()
  const { profileInfo } = useAuthentication()
  const dropzoneNftEl = useRef(null)
  const inputAvatarFile = useRef<any>(null)

  const [title, setTitle] = useState('')
  const [note, setNote] = useState('')
  const [assetUrl, setAssetUrl] = useState('')
  const [assetType, setAssetType] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [nftArt, setNftArt] = useState(null)
  const [alertData, setAlertData] = useState<any[]>([])
  const [showAlert, setShowAlert] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isStored, setIsStored] = useState(false)
  const [orgMerchInfo, setOrgMerchInfo] = useState<any>(null)
  const [enableSave, setEnableSave] = useState(false)

  useEffect(() => {
    if (profileInfo && !isStored) {
      let userId = profileInfo.id
      getStoreInfo(userId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileInfo])

  useEffect(() => {
    if (isStored) {
      if (orgMerchInfo === null) {
        setEnableSave(false)
        return
      }
      if (title !== orgMerchInfo.name) {
        setEnableSave(true)
        return
      }
      if (note !== orgMerchInfo.description) {
        setEnableSave(true)
        return
      }
      if (assetUrl !== orgMerchInfo.assetUrl) {
        setEnableSave(true)
        return
      } else if (nftArt !== null) {
        setEnableSave(true)
        return
      }
    } else {
      if (title !== '' || note !== '' || nftArt !== null) {
        setEnableSave(true)
        return
      }
    }

    setEnableSave(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, note, nftArt])

  const getStoreInfo = async userId => {
    merchAPI.getStoreInfo(userId).then(
      response => {
        if (response.message === undefined || response.message === null) {
          setIsStored(true)
          setTitle(response.name)
          setNote(response.description)
          setAssetUrl(response.assetUrl)
          setAssetType(response.assetType)
          setOrgMerchInfo(response)
        }
      },
      error => {}
    )
  }

  const handleSaveUserInfo = async () => {
    if (!profileInfo) return

    setIsSaving(true)
    let userId = profileInfo.id

    let url = '',
      type = ''
    if (assetUrl.length > 0) {
      url = assetUrl
      type = assetType
    } else if (nftArt !== null) {
      url = await uploadRawUrl(
        'store_' + userId + '_' + title,
        URL.createObjectURL(nftArt),
        nftArt.type,
        getFileTypeExt(nftArt)
      )
      type = nftArt.type
    }
    let params = {
      userId,
      name: title,
      description: note,
      assetUrl: url,
      assetType: type,
    }

    if (isStored) {
      merchAPI.updateStoreInfo(userId, params).then(
        response => {
          setIsSaving(false)
          setAssetUrl(url)
          setAssetType(params.assetType)
          setAlertData([
            {
              id: 0,
              title: 'Product Details Updated',
              description: 'Your store settings have been updated successfully',
              type: 'success',
            },
          ])
          setShowAlert(true)
          setTimeout(() => {
            onSave(response)
          }, 3000)
        },
        error => {}
      )
    } else {
      merchAPI.createStoreInfo(userId, params).then(
        response => {
          setIsSaving(false)
          setAssetUrl(url)
          setAssetType(params.assetType)
          setAlertData([
            {
              id: 0,
              title: 'Your store settings have been updated',
              description: 'Your store settings have been saved successfully',
              type: 'success',
            },
          ])
          setShowAlert(true)
          setTimeout(() => {
            onSave(response)
          }, 3000)
        },
        error => {}
      )
    }
  }

  const getFileTypeCategory = file => {
    return file.type.split('/')[0]
  }

  const getFileTypeExt = file => {
    return file.type.split('/')[1]
  }

  const uploadRawUrl = async (name: string, imageFile: string, fileType: string, fileExt: string) => {
    let response = await fetch(imageFile)
    let blob = await response.blob()
    const file = new File([blob], 'store.' + fileExt, {
      type: fileType,
    })
    let timestamp = new Date().getTime()
    let filename = name.replaceAll(' ', '') + timestamp
    const { url } = await uploadFileToS3(file, filename)
    return url
  }

  const handleShowAlert = error => {
    if (error === 'file-invalid-type') {
      let description = ''
      description = `Supported file formats .png, .jpeg`
      setAlertData([
        {
          id: 0,
          title: 'File Format Not Supported',
          description,
          type: 'error',
        },
      ])
    } else if (error === 'file-too-large') {
      setAlertData([
        {
          id: 0,
          title: 'Max File Size Exceeded',
          description: `Upload an image that is 2mb or less`,
          type: 'error',
        },
      ])
    } else {
      setAlertData([
        {
          id: 0,
          title: 'Unknown Error',
          description: `Unknown Error`,
          type: 'error',
        },
      ])
    }
    setTimeout(() => {
      setShowAlert(true)
    }, 500)
  }

  const closeModal = () => {
    close()
  }

  return (
    <div>
      <Transition show={open} as={Fragment}>
        <Dialog as="div" className={styles.container} onClose={() => closeModal()}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className={styles.bgView} />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex h-full lg:min-h-full items-center justify-center lg:p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className={styles.modalView}>
                  <div className={styles.close} onClick={() => closeModal()}>
                    <CloseIcon alt="" />
                  </div>
                  <div className={styles.editProfile}>Store Settings</div>

                  <div className="w-full h-[calc(100vh-220px)] sm:h-[calc(100vh-470px)] mt-[30px] overflow-auto pr-[16px] sm:pr-[41px]">
                    <div className={styles.subDesc}>
                      Make your merch store yours. Name it, describe it, and show it off with a preview image that
                      speaks your brand’s language.
                    </div>
                    <div className={styles.subTitle}>Store Information</div>

                    <div className="mt-[15px]">
                      <Input
                        error={errorMessage}
                        value={title}
                        fontSize={viewport === 'xs' || viewport === 'sm' || viewport === 'md' ? '14px' : '16px'}
                        color="var(--text-gray-200-color)"
                        backgroundColor="var(--bg-101010-F3F2F0)"
                        borderColor="rgba(var(--main-bg-color-rgb), 0.1)"
                        borderRadius="23px"
                        placeholder="Store Name"
                        onChange={value => {
                          if (errorMessage.length > 0) setErrorMessage('')
                          setTitle(value)
                        }}
                      />
                    </div>
                    <div className="mt-[10px] lg:mt-[15px] h-fit">
                      <Textarea
                        value={note}
                        fontSize={viewport === 'xs' || viewport === 'sm' || viewport === 'md' ? '14px' : '16px'}
                        color="var(--text-gray-200-color)"
                        backgroundColor="var(--bg-101010-F3F2F0)"
                        borderColor="rgba(var(--main-bg-color-rgb), 0.1)"
                        borderRadius="23px"
                        placeholder="Store Description"
                        width="100%"
                        height="142px"
                        // label01="Max Characters 1000"
                        // label02={1000 - note?.length === 1000 ? '' : `${1000 - note?.length} Characters left`}
                        onChange={value => setNote(value)}
                      />
                    </div>

                    <div className={styles.subTitle}>Social Preview</div>

                    <div className="mt-[10px] lg:mt-[15px]">
                      {assetUrl.length > 0 ? (
                        <div className={styles.artArea}>
                          {assetType.includes('video') ? (
                            <video controls className="w-full h-full z-10" id="video">
                              <source src={assetUrl} type={assetType} />
                              Your browser does not support the video tag.
                            </video>
                          ) : (
                            <img src={assetUrl} className="w-full h-full object-cover" />
                          )}
                          <div className={styles.editorIcon} onClick={() => setAssetUrl('')}>
                            <TrashBin />
                          </div>
                        </div>
                      ) : nftArt === null ? (
                        <Dropzone
                          onDropAccepted={acceptedFiles => {
                            acceptedFiles.length !== 0 ? setNftArt(acceptedFiles[0]) : handleShowAlert('unknown')
                          }}
                          onDropRejected={rejectedFiles => {
                            handleShowAlert(rejectedFiles[0]?.errors[0]?.code)
                          }}
                          accept={{
                            'image/jpeg': [],
                            'image/png': [],
                            'image/gif': [],
                          }}
                          maxFiles={1}
                          maxSize={2097152}
                        >
                          {({ getRootProps, getInputProps, open }) => (
                            <section className={styles.dropzone}>
                              <button onClick={open} ref={dropzoneNftEl} style={{ display: 'none' }}>
                                open
                              </button>
                              <div {...getRootProps()} className={styles.selectArea}>
                                <input {...getInputProps()} />
                                <p className={styles.uploadText}>Upload file</p>
                                <p className={styles.supportText}>Recommended Dimensions: 1920 x 1080</p>
                                <p className={styles.greenText}>Max File Size 2mb</p>
                              </div>
                            </section>
                          )}
                        </Dropzone>
                      ) : (
                        <div className={styles.artArea}>
                          {getFileTypeCategory(nftArt) === 'video' ? (
                            <video controls className="w-full h-full z-10" id="video">
                              <source src={URL.createObjectURL(nftArt)} type={nftArt.type} />
                              Your browser does not support the video tag.
                            </video>
                          ) : (
                            <img src={URL.createObjectURL(nftArt)} className="w-full h-full object-cover" />
                          )}
                          <div className={styles.editorIcon} onClick={() => inputAvatarFile.current.click()}>
                            <EditorGreenIcon />
                          </div>
                          <input
                            type="file"
                            id="avatar-file"
                            accept="image/png, image/jpeg, image/gif"
                            ref={inputAvatarFile}
                            style={{ display: 'none' }}
                            onChange={event => {
                              const files = event.target.files
                              if (files?.length === 0) return
                              setNftArt(files[0])
                              setAssetUrl('')
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-[30px] lg:mt-[30px] mb-[100px] lg:mb-[158px] w-full flex justify-center lg:justify-end items-center gap-6 pr-[16px] sm:pr-[45px]">
                    {isSaving ? (
                      <LoadingButton fullWidth height={50} borderRadius={25} />
                    ) : (
                      <GradientButton
                        label={'Save'}
                        fullWidth
                        height={50}
                        borderRadius={25}
                        disabled={!enableSave}
                        onClick={() => {
                          handleSaveUserInfo()
                        }}
                      />
                    )}
                  </div>

                  <Alert
                    open={showAlert}
                    close={() => {
                      setShowAlert(false)
                    }}
                    data={alertData}
                  />
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  )
}

export default EditMerchModal
